import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate } from '../../tools/calendars';
import { formatMoney, normalizeCurrency } from '../../tools/currencies';

// How a MIS document's date and money are SHOWN in a list, a card or a tab - in the document's
// own calendar and its own currency, exactly as the printed document writes them
// (api/utils/invoiceTemplate.js). A document written before either was selectable carries neither
// field and reads as Gregorian dirhams, which is what it always was.

/** A stored document day as that document writes it: 17/10/2026, 1405/07/25 SH, 06/05/1448 AH. */
export const showDocDate = (date, system, lang = 'en') => (date ? formatDate(date, system, { lang }) : '—');

/** 3,084.48 - or 1,250,000 for a rial. */
export const showMoney = (amount, currency) => formatMoney(amount, currency);

/** The currency code to print beside an amount (AED when the document names none). */
export const showCurrency = (currency) => normalizeCurrency(currency);

/** The two-letter UI language ('en' | 'ar' | 'fa') from an i18n instance. */
export const uiLang = (i18n) => {
  const code = ((i18n && i18n.language) || 'en').slice(0, 2);
  return ['en', 'ar', 'fa'].includes(code) ? code : 'en';
};

/** A packing list row's sort letter. S was renamed E: a list saved before still holds S, and reads as E (the server's normalizeSort). */
export const stoneSort = (value) => {
  const letter = String(value || '').trim().toUpperCase();
  return letter === 'S' ? 'E' : letter;
};

/**
 * The language a document is shown and printed in: ALWAYS the app's language to begin with. A language picked beside the print button
 * holds for the record it was picked on and is never remembered, so opening another record, opening this one again or changing the
 * app's language all go back to the app's. `record` is what identifies the document on screen.
 */
export function useDocLanguage(record) {
  const { i18n } = useTranslation();
  const appLang = uiLang(i18n);
  const [pick, setPick] = useState(null);
  useEffect(() => { setPick((cur) => (cur && cur.record !== record ? null : cur)); }, [record]);
  const lang = pick && pick.record === record && pick.app === appLang ? pick.lang : appLang;
  return [lang, (code) => setPick({ lang: code, record, app: appLang })];
}

/**
 * The three things every document row needs, bound to the UI language:
 *   fmt.date(day, system)      17/10/2026 · 1405/07/25 SH · 06/05/1448 AH
 *   fmt.money(amount, code)    3,084.48 · 1,250,000
 *   fmt.code(code)             AED, SAR, IRR ...
 */
export function useDocFormat() {
  const { i18n } = useTranslation();
  const lang = uiLang(i18n);
  // the same object while the language stays: a memoized row that is handed it then redraws only when something it shows changed
  return useMemo(() => ({
    lang,
    date: (date, system) => showDocDate(date, system, lang),
    money: showMoney,
    code: showCurrency,
  }), [lang]);
}
